import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { reportsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { fmt, dateForPeriod } from "./reportUtils";

interface BalanceSheetReportProps {
  period: string;
}

const BalanceSheetReport: React.FC<BalanceSheetReportProps> = ({ period }) => {
  const { currentBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    if (!currentBuilding?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const asOfDate = dateForPeriod(period);
        const data = await reportsApi.getBalanceSheet(
          currentBuilding.id,
          asOfDate,
        );
        if (!cancelled) setReport(data);
      } catch (err) {
        console.error("Error fetching balance sheet:", err);
        if (!cancelled)
          setError("Failed to load balance sheet. Please try again later.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentBuilding?.id, period]);

  if (loading) {
    return <Spinner message="Loading balance sheet..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  if (!report) {
    return <ErrorAlert message="No balance sheet data available" />;
  }

  const { assets, liabilities, equity, totalLiabilitiesAndEquity, balanced } =
    report;

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Balance Sheet
      </h2>
      <p className="text-sm text-gray-500 mb-6">As of {report.asOfDate}</p>

      {/* Assets & Liabilities Side-by-Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Assets */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg font-medium text-gray-900">Assets</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
              <LineItem label="Cash at Bank" amount={assets?.cashAtBank} />
              <LineItem label="Reserve Funds" amount={assets?.reserveFunds} />
              <LineItem
                label="Service Charge Debtors"
                amount={assets?.serviceChargeDebtors}
              />
              {(assets?.other || []).map((item: any) => (
                <LineItem
                  key={item.name}
                  label={item.name}
                  amount={item.amount}
                />
              ))}
              <div className="border-t-2 border-gray-300 pt-4 flex justify-between">
                <span className="text-sm font-semibold text-gray-900">
                  Total Assets
                </span>
                <span className="text-lg font-bold text-primary-600 tabular-nums">
                  {fmt(assets?.total || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Liabilities & Equity */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg font-medium text-gray-900">
              Liabilities & Equity
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  Liabilities
                </p>
                <div className="space-y-3 ml-2">
                  <LineItem
                    label="Service Charge Pool (held on trust)"
                    amount={liabilities?.serviceChargePool}
                  />
                  <LineItem
                    label="Unpaid Invoices"
                    amount={liabilities?.unpaidInvoices}
                  />
                  {(liabilities?.other || []).map((item: any) => (
                    <LineItem
                      key={item.name}
                      label={item.name}
                      amount={item.amount}
                    />
                  ))}
                  <div className="border-t border-gray-200 pt-3 flex justify-between">
                    <span className="text-sm font-semibold text-gray-900">
                      Total Liabilities
                    </span>
                    <span className="text-sm font-bold text-red-600 tabular-nums">
                      {fmt(liabilities?.total || 0)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  Equity
                </p>
                <div className="space-y-2 ml-2 border-t border-gray-200 pt-3">
                  <LineItem
                    label="Retained Earnings"
                    amount={equity?.retained}
                  />
                  {(equity?.other || []).map((item: any) => (
                    <LineItem
                      key={item.name}
                      label={item.name}
                      amount={item.amount}
                    />
                  ))}
                  <div className="border-t-2 border-gray-300 pt-2 flex justify-between">
                    <span className="text-sm font-semibold text-gray-900">
                      Total Equity
                    </span>
                    <span className="text-sm font-bold text-green-600 tabular-nums">
                      {fmt(equity?.total || 0)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t-2 border-gray-300 pt-4 flex justify-between">
                <span className="text-sm font-semibold text-gray-900">
                  Total Liab. & Equity
                </span>
                <span className="text-lg font-bold text-primary-600 tabular-nums">
                  {fmt(totalLiabilitiesAndEquity || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Balance Status */}
      <div
        className={`rounded-lg p-4 ${
          balanced
            ? "bg-green-50 border-l-4 border-green-500"
            : "bg-red-50 border-l-4 border-red-500"
        }`}
      >
        <p
          className={`text-sm font-medium ${
            balanced ? "text-green-800" : "text-red-800"
          }`}
        >
          {balanced
            ? "✓ Balance Sheet is balanced"
            : "⚠ Balance Sheet does not balance — please review"}
        </p>
      </div>
    </div>
  );
};

const LineItem: React.FC<{ label: string; amount?: number }> = ({
  label,
  amount,
}) => (
  <div className="flex justify-between">
    <span className="text-sm font-medium text-gray-600">{label}</span>
    <span className="text-sm font-semibold text-gray-900 tabular-nums">
      {fmt(amount || 0)}
    </span>
  </div>
);

export default BalanceSheetReport;
